import { useCallback, useEffect, useRef } from "react";

import { useRealtime } from "../use-realtime";

type UseRealtimeOptions = Parameters<typeof useRealtime>[0];

/**
 * Configuration for realtime workflow observation with polling fallback.
 */
export interface WorkflowObservationConfig {
	/** Whether the observation is active */
	enabled?: boolean;
	/** Realtime channels to subscribe to */
	channels: UseRealtimeOptions["channels"];
	/** Realtime event names to observe */
	events: string[];
	/** Handler for incoming realtime data */
	onData: (payload: { event: string; data: unknown; channel?: string }) => void;
	/** Optional polling function for fallback */
	poll?: () => Promise<void> | void;
	/** Polling interval in milliseconds */
	pollIntervalMs?: number;
	/** Idle time before polling fallback engages */
	idleTimeoutMs?: number;
}

/**
 * Observes realtime workflow events with an optional polling fallback.
 *
 * @param config - Observation configuration
 */
export function useWorkflowObservation(config: WorkflowObservationConfig): void {
	const {
		enabled = true,
		channels,
		events,
		onData,
		poll,
		pollIntervalMs = 10000,
		idleTimeoutMs = 15000,
	} = config;

	const lastRealtimeAtRef = useRef<number | null>(null);

	const handleRealtimeData = useCallback(
		(payload: { event: string; data: unknown; channel?: string }) => {
			lastRealtimeAtRef.current = Date.now();
			onData(payload);
		},
		[onData],
	);

	useRealtime({
		enabled,
		channels,
		events: events as UseRealtimeOptions["events"],
		onData: handleRealtimeData as UseRealtimeOptions["onData"],
	});

	useEffect(() => {
		if (!enabled || !poll) return;

		lastRealtimeAtRef.current = Date.now();

		const intervalId = window.setInterval(() => {
			const lastRealtimeAt = lastRealtimeAtRef.current;
			const idleFor = lastRealtimeAt ? Date.now() - lastRealtimeAt : idleTimeoutMs;

			if (idleFor >= idleTimeoutMs) {
				void poll();
			}
		}, pollIntervalMs);

		return () => {
			window.clearInterval(intervalId);
		};
	}, [enabled, poll, pollIntervalMs, idleTimeoutMs]);
}
